import React from 'react';
import styled from 'styled-components';
import { supabase } from '../../services/supabase';

export type DraftRecord = {
  id: string;
  name: string;
  subject: string;
  header_block: string;
  body_block: string;
  footer_block: string;
  html_body: string;
  text_body: string;
  updated_at: string;
};

export type ComposerRecipientScope = 'all' | 'filtered' | 'selected' | 'single';

type ComposerValues = {
  draftId?: string | null;
  draftName: string;
  subject: string;
  headerBlock: string;
  bodyBlock: string;
  footerBlock: string;
  htmlBody: string;
  textBody: string;
  recipientScope: ComposerRecipientScope;
};

type ScopeCountMap = Record<ComposerRecipientScope, number>;

type Props = {
  open: boolean;
  variant?: 'modal' | 'page';
  /** When `variant` is `page`, hides the dialog-style title row so the parent can own the page header. */
  embeddedInParentPage?: boolean;
  mode: 'single' | 'bulk';
  eventTitle: string;
  drafts: DraftRecord[];
  loadingDrafts?: boolean;
  sending?: boolean;
  savingDraft?: boolean;
  initialValues?: Partial<ComposerValues>;
  counts: ScopeCountMap;
  onClose: () => void;
  onSaveDraft: (payload: ComposerValues) => Promise<void>;
  onSend: (payload: ComposerValues) => Promise<void>;
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 1300;
  padding: 1rem;
`;

const PageContainer = styled.div`
  width: 100%;
  padding: 0;
  margin-top: 2rem;
`;

const PanelHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 0.75rem;
`;

const PanelTitle = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const PanelSubtitle = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--onSurfaceVariant);
`;

const TokenSectionLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 0.35rem;
`;

const ModalFooter = styled.div<{ $spread?: boolean; $variant: 'modal' | 'page' }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: ${({ $spread }) => ($spread ? 'space-between' : 'flex-end')};
  gap: 0.6rem;
  flex-wrap: wrap;
  padding: ${({ $variant }) =>
    $variant === 'modal' ? '1rem 1.2rem 1.15rem' : '1rem 0 0'};
  margin-top: ${({ $variant }) => ($variant === 'page' ? '0.35rem' : '0')};
  border-top: none;
  background: transparent;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const Modal = styled.div<{ $variant: 'modal' | 'page' }>`
  width: ${({ $variant }) => ($variant === 'page' ? '100%' : 'min(1000px, 100%)')};
  max-height: ${({ $variant }) => ($variant === 'modal' ? 'calc(100vh - 2rem)' : 'none')};
  display: flex;
  flex-direction: column;
  overflow: ${({ $variant }) => ($variant === 'modal' ? 'hidden' : 'visible')};
  background: ${({ $variant }) => ($variant === 'modal' ? 'var(--surface)' : 'transparent')};
  border: none;
  border-radius: 20px;
  box-shadow: ${({ $variant }) =>
    $variant === 'modal' ? '0 24px 48px rgba(15, 23, 42, 0.14)' : 'none'};
`;

const ModalAccent = styled.div`
  height: 4px;
  width: 100%;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--primary) 75%, transparent) 0%,
    color-mix(in srgb, var(--primary) 35%, transparent) 55%,
    color-mix(in srgb, var(--primary) 15%, transparent) 100%
  );
  flex-shrink: 0;
`;

const ModalBody = styled.div<{ $variant: 'modal' | 'page'; $compactTop?: boolean }>`
  padding: ${({ $variant, $compactTop }) => {
    const top = $compactTop ? '1rem' : '1.35rem';
    const horizontal = $variant === 'modal' ? '1.2rem' : '0';
    return `${top} ${horizontal} 1rem`;
  }};
  overflow: ${({ $variant }) => ($variant === 'modal' ? 'auto' : 'visible')};
  overflow-x: hidden;
  flex: ${({ $variant }) => ($variant === 'modal' ? 1 : 'initial')};
  min-height: ${({ $variant }) => ($variant === 'modal' ? 0 : 'auto')};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  margin-bottom: 0.35rem;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--onSurface);
`;

const TitleMeta = styled.p`
  margin: 0.35rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
  line-height: 1.45;
  word-break: break-word;
`;

const Helper = styled.p`
  margin: 0.35rem 0 0.85rem;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  line-height: 1.45;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(0, 0.92fr);
  gap: 1rem;
  align-items: start;
  & > * {
    min-width: 0;
  }
  @media (max-width: 980px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Panel = styled.section`
  border-radius: 16px;
  padding: 1rem 1.1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: var(--surface);
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.04);
`;

const Section = styled.section`
  border: none;
  border-radius: 0;
  padding: 0.75rem 0 0;
  margin: 0;
  background: transparent;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
`;

const Label = styled.label`
  display: block;
  color: var(--onSurface);
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  padding: 0.58rem 0.75rem;
  border-radius: 11px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  color: var(--onSurface);
  background: var(--surface);
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
    box-shadow:
      inset 0 1px 2px rgba(15, 23, 42, 0.04),
      0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }
`;

const Select = styled.select`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  padding: 0.58rem 0.75rem;
  border-radius: 11px;
  border: 1px solid var(--outline);
  font-size: 0.88rem;
  color: var(--onSurface);
  background: var(--surface);
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }
`;

const TextArea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-height: 88px;
  padding: 0.58rem 0.75rem;
  border-radius: 11px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  color: var(--onSurface);
  background: var(--surface);
  resize: vertical;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  line-height: 1.45;
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
    box-shadow:
      inset 0 1px 2px rgba(15, 23, 42, 0.04),
      0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }
`;

const Spacer = styled.div`
  height: 0.65rem;
`;

const ScopeSegment = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid var(--outline);
  overflow: hidden;
  background: var(--surfaceVariant);
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ScopeButton = styled.button<{ $active: boolean }>`
  min-width: 0;
  border: none;
  background: ${({ $active }) =>
    $active ? 'var(--surface)' : 'transparent'};
  color: ${({ $active }) => ($active ? 'var(--onSurface)' : 'var(--onSurfaceVariant)')};
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  padding: 0.5rem 0.45rem;
  cursor: pointer;
  transition: background 0.12s ease;
  box-shadow: ${({ $active }) => ($active ? 'inset 0 -2px 0 color-mix(in srgb, var(--primary) 45%, transparent)' : 'none')};
  border-right: 1px solid var(--outline);
  &:nth-child(3n) {
    border-right: none;
  }
  @media (max-width: 520px) {
    border-right: none;
    border-bottom: 1px solid var(--outline);
    &:last-child {
      border-bottom: none;
    }
  }
  &:hover {
    background: ${({ $active }) => ($active ? 'var(--surface)' : 'color-mix(in srgb, var(--surface) 50%, transparent)')};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 12px;
  padding: 0.52rem 0.95rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
    border-color: color-mix(in srgb, var(--onSurface) 12%, var(--outline));
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  border-color: color-mix(in srgb, var(--primary) 42%, var(--outline));
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--primary) 18%, var(--surface)) 0%,
    color-mix(in srgb, var(--primary) 10%, var(--surface)) 100%
  );
  box-shadow: 0 2px 10px color-mix(in srgb, var(--primary) 16%, transparent);
  &:hover:not(:disabled) {
    background: linear-gradient(
      180deg,
      color-mix(in srgb, var(--primary) 24%, var(--surface)) 0%,
      color-mix(in srgb, var(--primary) 14%, var(--surface)) 100%
    );
  }
`;

const TokenWrap = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: 0.35rem;
`;

const TokenButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--primary) 28%, var(--outline));
  background: color-mix(in srgb, var(--primary) 8%, var(--surface));
  color: var(--onSurfaceVariant);
  border-radius: 999px;
  padding: 0.22rem 0.55rem;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.12s ease,
    border-color 0.12s ease;
  &:hover {
    background: color-mix(in srgb, var(--primary) 14%, var(--surface));
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
    color: var(--onSurface);
  }
`;

const InlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.45rem;
`;

const InlineMessage = styled.div<{ $kind: 'error' | 'info' }>`
  font-size: 0.76rem;
  color: ${({ $kind }) => ($kind === 'error' ? 'var(--error)' : 'var(--onSurfaceVariant)')};
`;

const PreviewFrame = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: none;
  background: color-mix(in srgb, var(--surfaceVariant) 45%, var(--surface));
`;

const PreviewSubjectBar = styled.div`
  padding: 0.65rem 0.9rem;
  background: transparent;
  border-bottom: 1px solid color-mix(in srgb, var(--outline) 55%, transparent);
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PreviewSubjectKey = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.65rem;
  color: var(--onSurfaceVariant);
  flex-shrink: 0;
  margin-top: 0.12rem;
`;

const PreviewSubjectValue = styled.span`
  flex: 1;
  min-width: 0;
  color: var(--onSurface);
  font-weight: 600;
  line-height: 1.4;
`;

const PreviewBody = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 220px;
  padding: 1rem 0.95rem 1.05rem;
  color: var(--onSurface);
  font-size: 0.9rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.55;
  background: transparent;
`;

const PreviewTextChunk = styled.span`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const PreviewInlineImage = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 0.75rem 0;
  border: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  color: var(--onSurfaceVariant);
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--onSurface);
    border-color: color-mix(in srgb, var(--onSurface) 14%, var(--outline));
  }
`;

const templateTokens = [
  '{{full_name}}',
  '{{email}}',
  '{{ticket_type_name}}',
  '{{quantity}}',
  '{{registration_status}}',
  '{{event_title}}',
  '{{event_start_date}}',
  '{{event_start_time}}',
  '{{event_location}}',
  '{{calendar_url}}',
  '{{calendar_ics_url}}',
];

const EMAIL_IMAGE_BUCKET = 'post-images';
const MAX_UPLOAD_IMAGE_MB = 5;
const imageMarkerPattern = /\[\[image:([^\]]+)\]\]/gi;

const normalize = (value: string) => value.replace(/\r\n/g, '\n');
const appendImageMarker = (value: string, imageUrl: string) => {
  const marker = `[[image:${imageUrl}]]`;
  const normalized = normalize(value).trimEnd();
  return normalized ? `${normalized}\n\n${marker}` : marker;
};
const markersToPreviewText = (value: string) =>
  normalize(value).replace(imageMarkerPattern, (_whole, rawUrl: string) => {
    const trimmed = String(rawUrl || '').trim();
    return trimmed ? `[Image] ${trimmed}` : '[Image]';
  });

const combinePreviewBody = (header: string, body: string, footer: string) =>
  [markersToPreviewText(header).trim(), markersToPreviewText(body).trim(), markersToPreviewText(footer).trim()]
    .filter(Boolean)
    .join('\n\n');

const sanitizePreviewImageUrl = (value: string) => {
  try {
    const parsed = new URL(value.trim());
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

type PreviewChunk = { kind: 'text'; value: string } | { kind: 'image'; url: string };

const toPreviewChunks = (value: string): PreviewChunk[] => {
  const normalized = normalize(value).trim();
  if (!normalized) return [];

  const chunks: PreviewChunk[] = [];
  const markerRegex = /\[\[image:([^\]]+)\]\]/gi;
  let cursor = 0;
  let match = markerRegex.exec(normalized);

  while (match) {
    const before = normalized.slice(cursor, match.index);
    if (before) chunks.push({ kind: 'text', value: before });
    const imageUrl = sanitizePreviewImageUrl(String(match[1] || ''));
    chunks.push(imageUrl ? { kind: 'image', url: imageUrl } : { kind: 'text', value: match[0] });
    cursor = match.index + match[0].length;
    match = markerRegex.exec(normalized);
  }

  const tail = normalized.slice(cursor);
  if (tail) chunks.push({ kind: 'text', value: tail });
  return chunks;
};

const combinePreviewChunks = (header: string, body: string, footer: string): PreviewChunk[] => {
  const blocks = [header, body, footer].map(toPreviewChunks).filter((chunks) => chunks.length > 0);
  if (blocks.length === 0) return [];

  const combined: PreviewChunk[] = [];
  blocks.forEach((blockChunks, index) => {
    if (index > 0) combined.push({ kind: 'text', value: '\n\n' });
    combined.push(...blockChunks);
  });
  return combined;
};

const renderScopeLabel = (scope: ComposerRecipientScope, count: number) => {
  if (scope === 'single') return `Single registrant (${count})`;
  if (scope === 'selected') return `Selected only (${count})`;
  if (scope === 'filtered') return `Filtered results (${count})`;
  return `All registrants (${count})`;
};

const defaultValues: ComposerValues = {
  draftId: null,
  draftName: '',
  subject: '',
  headerBlock: '',
  bodyBlock: '',
  footerBlock: '',
  htmlBody: '',
  textBody: '',
  recipientScope: 'all',
};

const EventRegistrantEmailComposer: React.FC<Props> = ({
  open,
  variant = 'modal',
  embeddedInParentPage = false,
  mode,
  eventTitle,
  drafts,
  loadingDrafts = false,
  sending = false,
  savingDraft = false,
  initialValues,
  counts,
  onClose,
  onSaveDraft,
  onSend,
}) => {
  const [form, setForm] = React.useState<ComposerValues>(defaultValues);
  const [uploadingTarget, setUploadingTarget] = React.useState<'header' | 'body' | null>(null);
  const [imageNotice, setImageNotice] = React.useState<{ kind: 'error' | 'info'; message: string } | null>(null);
  const headerImageInputRef = React.useRef<HTMLInputElement | null>(null);
  const bodyImageInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setForm({
      ...defaultValues,
      ...initialValues,
      recipientScope: mode === 'single' ? 'single' : initialValues?.recipientScope ?? 'filtered',
    });
  }, [open, mode, initialValues]);

  const uploadBlockImage = async (target: 'header' | 'body', file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setImageNotice({ kind: 'error', message: 'Please choose an image file.' });
      return;
    }
    if (file.size > MAX_UPLOAD_IMAGE_MB * 1024 * 1024) {
      setImageNotice({ kind: 'error', message: `Please upload an image smaller than ${MAX_UPLOAD_IMAGE_MB}MB.` });
      return;
    }

    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `event-email-images/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

    setUploadingTarget(target);
    setImageNotice(null);
    try {
      const { error } = await supabase.storage.from(EMAIL_IMAGE_BUCKET).upload(fileName, file, {
        upsert: false,
        contentType: file.type || undefined,
      });
      if (error) throw error;
      const { data } = supabase.storage.from(EMAIL_IMAGE_BUCKET).getPublicUrl(fileName);
      const url = data.publicUrl;
      setForm((prev) =>
        target === 'header'
          ? { ...prev, headerBlock: appendImageMarker(prev.headerBlock, url) }
          : { ...prev, bodyBlock: appendImageMarker(prev.bodyBlock, url) }
      );
      setImageNotice({ kind: 'info', message: 'Image uploaded and inserted into the block.' });
    } catch (error) {
      setImageNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Could not upload image.',
      });
    } finally {
      setUploadingTarget(null);
      if (target === 'header' && headerImageInputRef.current) headerImageInputRef.current.value = '';
      if (target === 'body' && bodyImageInputRef.current) bodyImageInputRef.current.value = '';
    }
  };

  if (!open) return null;

  const selectedScope = mode === 'single' ? 'single' : form.recipientScope;
  const subjectPreview = form.subject.trim() || '(No subject)';
  const bodyPreview = combinePreviewBody(form.headerBlock, form.bodyBlock, form.footerBlock);
  const previewChunks = combinePreviewChunks(form.headerBlock, form.bodyBlock, form.footerBlock);
  const recipientCount = counts[selectedScope] ?? 0;
  const sendDisabled = sending || !form.subject.trim() || !bodyPreview.trim() || recipientCount < 1;

  const applyDraft = (draftId: string) => {
    const target = drafts.find((draft) => draft.id === draftId);
    if (!target) {
      setForm((prev) => ({
        ...prev,
        draftId: null,
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      draftId: target.id,
      draftName: target.name ?? '',
      subject: target.subject ?? '',
      headerBlock: target.header_block ?? '',
      bodyBlock: target.body_block ?? '',
      footerBlock: target.footer_block ?? '',
      htmlBody: target.html_body ?? '',
      textBody: target.text_body ?? '',
    }));
  };

  const appendTokenToBody = (token: string) => {
    setForm((prev) => ({
      ...prev,
      bodyBlock: `${prev.bodyBlock}${prev.bodyBlock ? ' ' : ''}${token}`,
    }));
  };

  const bulkScopes: ComposerRecipientScope[] = ['all', 'filtered', 'selected'];
  const pageChrome = variant === 'page' && embeddedInParentPage;

  const composer = (
    <Modal
      $variant={variant}
      role="dialog"
      aria-modal={variant === 'modal' ? 'true' : undefined}
      aria-labelledby={pageChrome ? undefined : 'event-email-composer-title'}
      aria-label={
        pageChrome
          ? mode === 'single'
            ? `Email composer for ${eventTitle}`
            : `Bulk email composer for ${eventTitle}`
          : undefined
      }
      onClick={variant === 'modal' ? (event) => event.stopPropagation() : undefined}
    >
      <ModalAccent />
      <ModalBody $variant={variant} $compactTop={pageChrome}>
        {pageChrome ? null : (
          <>
            <HeaderRow>
              <TitleBlock>
                <Title id="event-email-composer-title">{mode === 'single' ? 'Email registrant' : 'Send bulk email'}</Title>
                <TitleMeta>{eventTitle}</TitleMeta>
              </TitleBlock>
              <CloseButton type="button" onClick={onClose} aria-label="Close email composer">
                ×
              </CloseButton>
            </HeaderRow>
           
          </>
        )}
        <Grid>
          <Panel>
            <PanelHeading>
              <PanelTitle>Message</PanelTitle>
              <PanelSubtitle>
                {mode === 'bulk'
                  ? 'Pick a draft or start fresh, choose recipients, then write your subject and blocks.'
                  : 'Write your subject and message blocks; tokens fill in per registrant when sent.'}
              </PanelSubtitle>
            </PanelHeading>
            <Section>
              <Label htmlFor="event-email-draft-select">Load existing draft</Label>
              <Select
                id="event-email-draft-select"
                value={form.draftId ?? ''}
                onChange={(event) => applyDraft(event.target.value)}
                disabled={loadingDrafts || drafts.length === 0}
              >
                <option value="">Start from empty</option>
                {drafts.map((draft) => (
                  <option key={draft.id} value={draft.id}>
                    {draft.name || 'Untitled'} — {new Date(draft.updated_at).toLocaleString()}
                  </option>
                ))}
              </Select>
              <Spacer />

              {mode === 'bulk' ? (
                <>
                  <Label id="event-email-recipient-scope-label">Recipients</Label>
                  <ScopeSegment role="radiogroup" aria-labelledby="event-email-recipient-scope-label">
                    {bulkScopes.map((scope) => (
                      <ScopeButton
                        key={scope}
                        type="button"
                        $active={selectedScope === scope}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            recipientScope: scope,
                          }))
                        }
                      >
                        {renderScopeLabel(scope, counts[scope])}
                      </ScopeButton>
                    ))}
                  </ScopeSegment>
                  <Spacer />
                </>
              ) : null}

              <Label htmlFor="event-email-draft-name">Draft name</Label>
              <Input
                id="event-email-draft-name"
                value={form.draftName}
                placeholder="April reminder"
                onChange={(event) => setForm((prev) => ({ ...prev, draftName: event.target.value }))}
              />
              <Spacer />

              <Label htmlFor="event-email-subject">Subject</Label>
              <Input
                id="event-email-subject"
                value={form.subject}
                placeholder="Reminder for {{event_title}}"
                onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
              />
              <Spacer />

              <Label htmlFor="event-email-header">Header block</Label>
              <TextArea
                id="event-email-header"
                value={form.headerBlock}
                placeholder="Hello {{full_name}},"
                onChange={(event) => setForm((prev) => ({ ...prev, headerBlock: event.target.value }))}
              />
              <InlineActions>
                <input
                  ref={headerImageInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => {
                    void uploadBlockImage('header', event.target.files?.[0]);
                  }}
                />
                <Button
                  type="button"
                  onClick={() => headerImageInputRef.current?.click()}
                  disabled={uploadingTarget !== null}
                >
                  {uploadingTarget === 'header' ? 'Uploading image…' : 'Upload image to header'}
                </Button>
              </InlineActions>
              <Spacer />

              <Label htmlFor="event-email-body">Body block</Label>
              <TextArea
                id="event-email-body"
                value={form.bodyBlock}
                placeholder="Main message..."
                onChange={(event) => setForm((prev) => ({ ...prev, bodyBlock: event.target.value }))}
              />
              <InlineActions>
                <input
                  ref={bodyImageInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => {
                    void uploadBlockImage('body', event.target.files?.[0]);
                  }}
                />
                <Button type="button" onClick={() => bodyImageInputRef.current?.click()} disabled={uploadingTarget !== null}>
                  {uploadingTarget === 'body' ? 'Uploading image…' : 'Upload image to body'}
                </Button>
              </InlineActions>
              <TokenSectionLabel id="event-email-token-hint">Insert placeholders (tap to add at cursor area)</TokenSectionLabel>
              <TokenWrap role="group" aria-labelledby="event-email-token-hint">
                {templateTokens.map((token) => (
                  <TokenButton key={token} type="button" onClick={() => appendTokenToBody(token)}>
                    {token}
                  </TokenButton>
                ))}
              </TokenWrap>
              <Spacer />

              <Label htmlFor="event-email-footer">Footer block</Label>
              <TextArea
                id="event-email-footer"
                value={form.footerBlock}
                placeholder="Thanks, Event Team"
                onChange={(event) => setForm((prev) => ({ ...prev, footerBlock: event.target.value }))}
              />
            </Section>
          </Panel>

          <Panel>
            <PanelHeading>
              <PanelTitle>Preview</PanelTitle>
              <PanelSubtitle>Approximate layout; personalization and HTML happen when the message is sent.</PanelSubtitle>
            </PanelHeading>
            <Section>
              <PreviewFrame>
                <PreviewSubjectBar>
                  <PreviewSubjectKey>Subject</PreviewSubjectKey>
                  <PreviewSubjectValue>{subjectPreview}</PreviewSubjectValue>
                </PreviewSubjectBar>
                <PreviewBody>
                  {previewChunks.length === 0
                    ? '(No email body yet)'
                    : previewChunks.map((chunk, index) =>
                        chunk.kind === 'image' ? (
                          <PreviewInlineImage key={`preview-image-${index}`} src={chunk.url} alt="Email preview upload" />
                        ) : (
                          <PreviewTextChunk key={`preview-text-${index}`}>{chunk.value}</PreviewTextChunk>
                        )
                      )}
                </PreviewBody>
              </PreviewFrame>
              <Spacer />
              <Helper style={{ margin: 0 }}>
                {pageChrome
                  ? 'Sender name and HTML wrapping are applied by the server when you send.'
                  : 'Tokens personalize each message. Final HTML and sender details are applied by the secure edge function at send time.'}
              </Helper>
              {imageNotice ? <InlineMessage $kind={imageNotice.kind}>{imageNotice.message}</InlineMessage> : null}
            </Section>
          </Panel>
        </Grid>
      </ModalBody>
      <ModalFooter $variant={variant} $spread={pageChrome}>
        {pageChrome ? (
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
        ) : null}
        <FooterActions>
          <Button type="button" disabled={savingDraft || !form.subject.trim()} onClick={() => onSaveDraft(form)}>
            {savingDraft ? 'Saving draft…' : 'Save draft'}
          </Button>
          <PrimaryButton type="button" disabled={sendDisabled} onClick={() => onSend(form)}>
            {sending ? 'Sending…' : mode === 'single' ? 'Send email' : `Send bulk (${recipientCount})`}
          </PrimaryButton>
        </FooterActions>
      </ModalFooter>
    </Modal>
  );

  if (variant === 'page') {
    return <PageContainer>{composer}</PageContainer>;
  }

  return <Overlay onClick={onClose}>{composer}</Overlay>;
};

export default EventRegistrantEmailComposer;
